import React, { useEffect, useRef, useState } from "react";
import {
  User,
  UploadCloud,
  FileText,
  History,
  Download,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronDown,
  Clock,
  CalendarDays,
  Globe,
  Briefcase,
  MessageSquare,
  Send
} from "lucide-react";
import { Card, CardContent } from "../ui/CCard";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import api from "../../api";

function calculateAge(dob) {
  if (!dob) return "N/A";
  try {
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return "N/A";
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : "N/A";
  } catch (e) {
    return "N/A";
  }
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const initials = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0][0];
  return initials.toUpperCase();
}

function getLanguage(code) {
  const langs = {
    "en-US": "English", "hi-IN": "Hindi", "bn-IN": "Bengali",
    "ta-IN": "Tamil", "te-IN": "Telugu", "mr-IN": "Marathi",
    "kn-IN": "Kannada", "ur-IN": "Urdu", "sa-IN": "Sanskrit",
    "gu-IN": "Gujarati", "as-IN": "Assamese", "ml-IN": "Malayalam",
    "or-IN": "Odia", "pa-IN": "Punjabi"
  };
  return langs[code] || code || "English";
}

// Secondary, convenience surface for the two-way message thread - the
// primary, always-available one is Appointment.js's own modal (works
// regardless of whether a consultation exists yet / the same-day gate on
// starting one). Self-contained (fetches/sends on its own) rather than
// threaded through ConsultationNew.js/ConsultationEdit.js's already-large
// prop lists, since all it needs is the appointment id.
function MessagesCard({ appointmentId, gpAdvice, patientName }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [rewriting, setRewriting] = useState(false);

  useEffect(() => {
    if (!isOpen || !appointmentId) return;

    const fetchMessages = () => {
      api.getAppointmentMessages(appointmentId)
        .then((res) => setMessages(res.data || []))
        .catch(() => {});
    };

    setLoading(true);
    api.getAppointmentMessages(appointmentId)
      .then((res) => setMessages(res.data || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));

    // Refresh the message panel every one minute (60s) for the doctor on consultation page
    const interval = setInterval(fetchMessages, 60000);
    return () => clearInterval(interval);
  }, [isOpen, appointmentId]);

  const handleSend = () => {
    const body = newMessage.trim();
    if (!body || !appointmentId) return;
    setSending(true);
    api.sendAppointmentMessage(appointmentId, { message: body })
      .then((res) => {
        setMessages((prev) => [...prev, res.data]);
        setNewMessage("");
      })
      .catch(() => {})
      .finally(() => setSending(false));
  };

  const hasAdvice = Boolean(gpAdvice && gpAdvice.trim());

  // Rewrites the session's own Plan/advice notes (gp_advice) into a warm,
  // second-person message ready to send - never overwrites gp_advice
  // itself, just prefills the compose box so the doctor can review/edit
  // before sending. Disabled until there's actual advice to translate.
  const handleRewriteForPatient = () => {
    if (!hasAdvice || rewriting) return;
    setRewriting(true);
    api.rewriteAdviceForPatient({ advice: gpAdvice, patient_name: patientName })
      .then((res) => setNewMessage(res.data?.patient_summary || ""))
      .catch(() => {})
      .finally(() => setRewriting(false));
  };

  return (
    <Card className="shadow-sm !border-purple-200 !rounded-xl overflow-hidden">
      <div className="h-1.5 bg-purple-500" />
      <CardContent className="p-5">
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          className="w-full font-semibold text-sm text-gray-900 flex items-center justify-between cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-purple-50 flex items-center justify-center shrink-0">
              <MessageSquare className="w-3.5 h-3.5 text-purple-600" />
            </span>
            Messages
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && !appointmentId && (
          <p className="text-center text-[11px] text-gray-400 py-3 mt-3 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            No appointment linked yet.
          </p>
        )}

        {isOpen && appointmentId && (
          <div className="mt-3 space-y-2.5">
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
              {loading ? (
                <p className="text-center text-[11px] text-gray-400 py-3">Loading…</p>
              ) : messages.length === 0 ? (
                <p className="text-center text-[11px] text-gray-400 py-3">No messages yet.</p>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.id}
                    className={`p-2 rounded-lg border text-xs ${
                      m.sender_type === "doctor" ? "bg-primary-50 border-primary-100 ml-4" : "bg-white border-gray-100 mr-4"
                    }`}
                  >
                    <p className={`text-[9px] font-bold mb-0.5 ${m.sender_type === "doctor" ? "text-primary-600" : "text-amber-600"}`}>
                      {m.sender_type === "doctor" ? "You" : "From Patient"}
                    </p>
                    <p className="text-gray-800 whitespace-pre-wrap">{m.body}</p>
                    <p className="text-[9px] text-gray-400 mt-1">
                      {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleRewriteForPatient}
                disabled={!hasAdvice || rewriting}
                title={hasAdvice ? "" : "Fill in Patient Advice (in the Plan section) first"}
                className="text-[9px] font-semibold text-primary-600 hover:text-primary-800 hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
              >
                {rewriting ? "Rewriting…" : "✨ Rewrite Advice for Patient"}
              </button>
            </div>
            <div className="flex items-end gap-1.5">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message…"
                rows={2}
                className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!newMessage.trim() || sending}
                className="p-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-colors"
                title="Send"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ConsultationSidePanel({
  patient,
  onEditPatient,
  consultationId,
  appointmentId,
  gpAdvice,
  reports = [],
  reportName,
  setReportName,
  testOptions = [],
  linkedTestName,
  setLinkedTestName,
  uploadingReports,
  onFileUpload,
  onDownloadReport,
  onDeleteReport,
  currentUserId,
  history = [],
  onSelectHistoryItem,
}) {
  const fileInputRef = useRef(null);
  // Collapsed by default on page load - patient identity is what matters at a
  // glance; reports/history are reference info the user opens on demand.
  const [reportsOpen, setReportsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <div className="w-full xl:w-80 flex-shrink-0">
      <div className="sticky top-6 space-y-4 max-h-[calc(100vh-5rem)] overflow-y-auto pb-6 pr-1">

        {/* 1. PATIENT DETAILS CARD */}
        {patient && (patient.name || patient.id) && (
          <Card className="shadow-sm !border-primary-400 !rounded-xl overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700" />
            <CardContent className="p-5">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 text-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md font-bold text-sm tracking-wide">
                    {getInitials(patient.name)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-900 truncate" title={patient.name}>
                      {patient.name}
                    </h3>
                    <span className="text-xs font-medium text-gray-500 block truncate">
                      {patient.contact || "No contact"}
                    </span>
                  </div>
                </div>
                {onEditPatient && (
                  <button
                    type="button"
                    onClick={() => onEditPatient(patient)}
                    className="flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-secondary-900 text-xs font-bold px-3 py-1.5 rounded-full border border-emerald-100 transition-colors shrink-0"
                  >
                    Edit
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded-lg p-2.5 flex items-start gap-2">
                  <CalendarDays className="w-3.5 h-3.5 text-primary-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 leading-tight">Age</p>
                    <p className="text-xs font-bold text-gray-900 truncate">{calculateAge(patient.dob)} yrs</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2.5 flex items-start gap-2">
                  <User className="w-3.5 h-3.5 text-primary-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 leading-tight">Gender</p>
                    <p className="text-xs font-bold text-gray-900 truncate">{patient.gender || "N/A"}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2.5 flex items-start gap-2">
                  <Globe className="w-3.5 h-3.5 text-primary-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 leading-tight">Language</p>
                    <p className="text-xs font-bold text-gray-900 truncate">{getLanguage(patient.lang)}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2.5 flex items-start gap-2">
                  <Briefcase className="w-3.5 h-3.5 text-primary-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 leading-tight">Occupation</p>
                    <p className="text-xs font-bold text-gray-900 truncate">{patient.occupation || "N/A"}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 2. REPORTS & ATTACHMENTS CARD - accent (teal) distinguishes this from the patient/history cards */}
        <Card className="shadow-sm !border-accent-200 !rounded-xl overflow-hidden">
          <div className="h-1.5 bg-accent-500" />
          <CardContent className="p-5">
            <button
              type="button"
              onClick={() => setReportsOpen((o) => !o)}
              className="w-full font-semibold text-sm text-gray-900 flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-accent-50 flex items-center justify-center shrink-0">
                  <FileText className="w-3.5 h-3.5 text-accent-600" />
                </span>
                Reports
              </span>
              <span className="flex items-center gap-1.5">
                {reports.length > 0 && (
                  <span className="bg-accent-50 text-accent-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    {reports.length}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${reportsOpen ? "rotate-180" : ""}`} />
              </span>
            </button>

            {reportsOpen && (
            <div className="mt-3">
            {consultationId ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 space-y-3">
                <div className="space-y-2">
                  {testOptions.length > 0 && setLinkedTestName && (
                    <select
                      value={linkedTestName || ""}
                      onChange={(e) => setLinkedTestName(e.target.value)}
                      disabled={uploadingReports}
                      className="w-full h-8 text-xs border border-gray-200 rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      title="Link this report to a suggested test"
                    >
                      <option value="">Other</option>
                      {testOptions.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  )}
                  {/* Free-text name only needed when there's nothing to pick
                      from, or the doctor explicitly picked "Other" (value ""). */}
                  {(testOptions.length === 0 || !linkedTestName) && (
                    <Input
                      type="text"
                      placeholder="Report Name (required)"
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                      disabled={uploadingReports}
                      className="text-xs bg-white h-8 border-gray-200 focus:border-primary-500"
                    />
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={onFileUpload}
                    disabled={uploadingReports}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (!reportName.trim() && !linkedTestName) {
                        alert("Please enter a report name or select a test before uploading the file.");
                        return;
                      }
                      fileInputRef.current?.click();
                    }}
                    disabled={uploadingReports || (!reportName.trim() && !linkedTestName)}
                    className="flex items-center bg-white shadow-sm w-full justify-center text-xs h-8"
                  >
                    {uploadingReports ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {uploadingReports ? "Uploading..." : "Upload File"}
                  </Button>
                </div>

                {reports.length > 0 ? (
                  <div className="space-y-2 pt-2 border-t border-gray-200">
                    <p className="text-[11px] font-medium text-gray-500">Attached Reports</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-0.5">
                      {reports.map((report) => (
                        <div
                          key={report.id}
                          className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-100 shadow-sm"
                        >
                          <div className="flex items-center overflow-hidden flex-1 mr-2 min-w-0">
                            <FileText className="w-3.5 h-3.5 text-primary-500 mr-2 flex-shrink-0" />
                            <div className="truncate">
                              <p className="text-xs font-medium text-gray-800 truncate" title={report.file_name}>
                                {report.file_name}
                              </p>
                              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                <p className="text-[10px] text-gray-400">
                                  {report.created_at ? new Date(report.created_at).toLocaleDateString() : ""}
                                </p>
                                {report.uploaded_by === "patient" && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                    From Patient
                                  </span>
                                )}
                                {report.linked_test_name && (
                                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 truncate max-w-[110px]" title={report.linked_test_name}>
                                    {report.linked_test_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {onDownloadReport && (
                              <button
                                onClick={() => onDownloadReport(report.id)}
                                className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                title="Download"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {onDeleteReport && (() => {
                              // Mirrors the backend's own rule (delete_consultation_report):
                              // only the uploader may delete their own report. A patient
                              // upload is never deletable here at all; a legacy report with
                              // no recorded uploader (uploaded_by_user_id null) falls back
                              // to allowing it, since there's nothing to check against.
                              const canDelete =
                                report.uploaded_by !== "patient" &&
                                (report.uploaded_by_user_id == null ||
                                  Number(report.uploaded_by_user_id) === Number(currentUserId));
                              if (!canDelete) return null;
                              return (
                                <button
                                  onClick={() => onDeleteReport(report.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete report"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-[11px] text-gray-400 py-3 border border-dashed border-gray-200 rounded-lg bg-white">
                    No reports attached yet.
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-4 px-3 text-xs text-gray-400 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                <FileText className="w-5 h-5 mx-auto text-gray-300 mb-1.5" />
                <p>Save consultation first to enable report uploads.</p>
              </div>
            )}
            </div>
            )}
          </CardContent>
        </Card>

        {/* 3. MESSAGES CARD - two-way thread with the patient, scoped to this appointment */}
        <MessagesCard appointmentId={appointmentId} gpAdvice={gpAdvice} patientName={patient?.name} />

        {/* 4. PATIENT CONSULTATION HISTORY CARD - neutral slate distinguishes this from the patient/reports cards */}
        <Card className="shadow-sm !border-secondary-400 !rounded-xl overflow-hidden">
          <div className="h-1.5 bg-secondary-400" />
          <CardContent className="p-5">
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              className="w-full font-semibold text-sm text-gray-900 flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-secondary-100 flex items-center justify-center shrink-0">
                  <History className="w-3.5 h-3.5 text-secondary-600" />
                </span>
                Consultation History
              </span>
              <span className="flex items-center gap-1.5">
                {history.length > 0 && (
                  <span className="bg-secondary-100 text-secondary-700 text-xs px-2 py-0.5 rounded-full font-medium">
                    {history.length}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
              </span>
            </button>

            {historyOpen && (
            <div className="mt-3">
            {history.length > 0 ? (
              <div className="relative max-h-60 overflow-y-auto pr-0.5 pl-4">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200" />
                <div className="space-y-2.5">
                  {history.map((appt) => {
                    const isApptGP = appt.consultation_type === "general_medicine";
                    return (
                      <button
                        key={appt.id}
                        type="button"
                        onClick={() => onSelectHistoryItem && onSelectHistoryItem(appt)}
                        className="relative w-full text-left p-2.5 rounded-lg border border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 transition-colors flex items-center justify-between group bg-white"
                      >
                        <span className={`absolute -left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ring-2 ring-white ${
                          isApptGP ? "bg-teal-500" : "bg-purple-500"
                        }`} />
                        <div className="min-w-0 pr-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-xs text-gray-900 group-hover:text-primary-700">
                              {appt.sessiondate || "Unknown Date"}
                            </span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${
                              isApptGP ? "bg-teal-100 text-teal-700" : "bg-purple-100 text-purple-700"
                            }`}>
                              {isApptGP ? "GP" : "MH"}
                            </span>
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{appt.sessiontime || "Time unrecorded"}</span>
                            {appt.sessionduration && <span>({appt.sessionduration}m)</span>}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary-600 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-center text-xs text-gray-400 py-3 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                No past consultations found.
              </p>
            )}
            </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
